<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import Badge from '@/components/ui/Badge.vue';
import UISelect from '@/components/ui/UISelect.vue';
import Combobox from '@/components/v2/Combobox.vue';
import type { ComboboxOption } from '@/components/v2/Combobox.vue';
import { Clock, Eye, X } from 'lucide-vue-next';
import type { Document } from '@/types/ui/PromptManager';
import { documentService } from '@/services/v2/DocumentService';
import { accountService } from '@/services/api';
import type { AccountSummary } from '@/types/AccountSummary';
import type { DocumentType, Document as PlatformDocument } from '@/types/v2/Document';
import DocumentViewerModal from '@/components/DocumentViewerModal.vue';

interface Props {
  selectedDocument: Document | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  documentSelect: [document: Document | null];
}>();

// State
const accounts = ref<AccountSummary[]>([]);
const documentTypes = ref<DocumentType[]>([]);
const documents = ref<PlatformDocument[]>([]);
const allAccountDocuments = ref<PlatformDocument[]>([]); // All documents for selected account (for filtering)
const selectedAccountId = ref<string>('');
const selectedDocumentTypeId = ref<string>('');
const isLoadingAccounts = ref(false);
const isLoadingDocumentTypes = ref(false);
const isLoadingDocuments = ref(false);
const showDocumentModal = ref(false);
const viewDocumentFileName = ref('');
const viewDocument = ref<PlatformDocument | undefined>(undefined);

// Computed
const accountOptions = computed<ComboboxOption[]>(() => {
  return accounts.value.map((account) => ({
    label: account.accountName,
    value: account.accountID.toString(),
  }));
});

const policyDocumentTypes = computed(() => {
  return documentTypes.value.filter((type) => type.domainName === 'Policy').sort((a, b) => a.name.localeCompare(b.name));
});

// Filter document types to only those that appear in the allAccountDocuments list
const availableDocumentTypes = computed(() => {
  if (allAccountDocuments.value.length === 0) {
    return [];
  }

  // Get unique document type IDs from all account documents (not filtered)
  const documentTypeIdsInDocuments = new Set(
    allAccountDocuments.value.filter((doc) => doc.documentDomainName === 'Policy' && doc.documentTypeId).map((doc) => doc.documentTypeId),
  );

  // Filter policy document types to only those that have documents
  return policyDocumentTypes.value.filter((type) => documentTypeIdsInDocuments.has(type.id));
});

// Count documents per document type (based on all account documents)
const documentTypeCounts = computed(() => {
  const counts = new Map<number, number>();
  allAccountDocuments.value
    .filter((doc) => doc.documentDomainName === 'Policy' && doc.documentTypeId)
    .forEach((doc) => {
      const currentCount = counts.get(doc.documentTypeId!) || 0;
      counts.set(doc.documentTypeId!, currentCount + 1);
    });
  return counts;
});

const documentTypeOptions = computed(() => {
  return availableDocumentTypes.value.map((type) => {
    const count = documentTypeCounts.value.get(type.id) || 0;
    return {
      label: type.name,
      description: `${count} document${count === 1 ? '' : 's'}`,
      value: type.id.toString(),
    };
  });
});

const filteredDocuments = computed(() => {
  const accountIdNum = selectedAccountId.value ? parseInt(selectedAccountId.value) : null;
  const docTypeIdNum = selectedDocumentTypeId.value ? parseInt(selectedDocumentTypeId.value) : null;

  return documents.value.filter((doc) => {
    // Filter by Policy domain
    if (doc.documentDomainName !== 'Policy') return false;

    // Filter by account if selected
    if (accountIdNum !== null && doc.accountId !== accountIdNum) {
      return false;
    }

    // Filter by document type if selected
    if (docTypeIdNum !== null && doc.documentTypeId !== docTypeIdNum) {
      return false;
    }

    return true;
  });
});

// Methods
const loadAccounts = async () => {
  isLoadingAccounts.value = true;
  try {
    accounts.value = await accountService.list();
  } catch (error) {
    console.error('Failed to load accounts:', error);
  } finally {
    isLoadingAccounts.value = false;
  }
};

const loadDocumentTypes = async () => {
  isLoadingDocumentTypes.value = true;
  try {
    documentTypes.value = await documentService.getDocumentTypes();
  } catch (error) {
    console.error('Failed to load document types:', error);
  } finally {
    isLoadingDocumentTypes.value = false;
  }
};

const loadDocuments = async () => {
  if (!selectedAccountId.value) {
    documents.value = [];
    allAccountDocuments.value = [];
    return;
  }

  isLoadingDocuments.value = true;
  try {
    const accountIdNum = parseInt(selectedAccountId.value);

    // Get Policy domain ID
    const domains = await documentService.getDocumentDomains();
    const policyDomain = domains.find((d) => d.name === 'Policy');

    // Always load all Policy documents for the account (for document type filtering)
    const allDocsRequest: { documentDomainId?: number; accountId?: number } = {
      accountId: accountIdNum,
    };
    if (policyDomain) {
      allDocsRequest.documentDomainId = policyDomain.id;
    }
    allAccountDocuments.value = await documentService.getDocuments(allDocsRequest);

    // Apply document type filter if selected
    if (selectedDocumentTypeId.value) {
      const docTypeIdNum = parseInt(selectedDocumentTypeId.value);
      documents.value = allAccountDocuments.value.filter((doc) => doc.documentTypeId === docTypeIdNum);
    } else {
      documents.value = allAccountDocuments.value;
    }
  } catch (error) {
    console.error('Failed to load documents:', error);
    documents.value = [];
    allAccountDocuments.value = [];
  } finally {
    isLoadingDocuments.value = false;
  }
};

const handleAccountSelect = (value: string) => {
  selectedAccountId.value = value || '';
  selectedDocumentTypeId.value = ''; // Reset document type when account changes
  loadDocuments();
};

const handleDocumentTypeSelect = (value: string) => {
  selectedDocumentTypeId.value = value || '';
  // Filter documents locally instead of reloading from API
  if (!selectedDocumentTypeId.value) {
    documents.value = allAccountDocuments.value;
  } else {
    const docTypeIdNum = parseInt(selectedDocumentTypeId.value);
    documents.value = allAccountDocuments.value.filter((doc) => doc.documentTypeId === docTypeIdNum);
  }
};

const resetDocumentTypeFilter = () => {
  selectedDocumentTypeId.value = '';
  documents.value = allAccountDocuments.value;
};

const handleDocumentClick = (doc: PlatformDocument) => {
  const document: Document = {
    name: doc.displayName || doc.fileName,
    documentId: doc.documentId, // UUID string from Document.DocumentID
    accountId: doc.accountId || undefined,
    displayName: doc.displayName,
    documentTypeName: doc.documentTypeName,
    createdAt: doc.createdByDate,
    type: doc.fileName.split('.').pop() || undefined,
  };

  // Toggle selection if clicking the same document
  if (props.selectedDocument?.documentId === document.documentId) {
    emit('documentSelect', null);
  } else {
    emit('documentSelect', document);
  }
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getFileIcon = (fileName?: string) => {
  if (!fileName) return '📄';
  const ext = fileName.split('.').pop()?.toLowerCase();
  if (ext === 'pdf') return '📄';
  if (ext === 'docx' || ext === 'doc') return '📝';
  if (ext === 'txt') return '📄';
  return '📁';
};

const isDocumentSelected = (doc: PlatformDocument) => {
  return props.selectedDocument?.documentId === doc.documentId;
};

const openDocument = async (doc: PlatformDocument, event: Event) => {
  event.stopPropagation();

  if (!doc.documentId || !doc.accountId) {
    console.error('Document ID and Account ID are required for viewing');
    return;
  }

  viewDocumentFileName.value = doc.displayName || doc.fileName;
  viewDocument.value = doc;
  showDocumentModal.value = true;
};

const fetchDocument = async (convertToPdf: boolean): Promise<string> => {
  if (!viewDocument.value?.documentId || !viewDocument.value?.accountId) {
    throw new Error('No document selected for viewing');
  }

  try {
    return await documentService.downloadDocumentFile(viewDocument.value.accountId, viewDocument.value.documentId, convertToPdf);
  } catch (error: any) {
    console.error('Failed to fetch document for viewing:', error);
    throw error;
  }
};

// Lifecycle
onMounted(async () => {
  await Promise.all([loadAccounts(), loadDocumentTypes()]);
});
</script>

<template>
  <div class="tw:space-y-4">
    <!-- Account Dropdown -->
    <div class="tw:space-y-2">
      <label class="tw:text-sm tw:font-medium tw:text-slate-700">Client</label>
      <Combobox
        v-model="selectedAccountId"
        :options="accountOptions"
        placeholder="Search and select a client..."
        :disabled="isLoadingAccounts"
        @selected="(option) => handleAccountSelect(option.value)" />
    </div>

    <!-- Document Type Dropdown -->
    <div
      v-if="selectedAccountId"
      class="tw:space-y-2">
      <label class="tw:text-sm tw:font-medium tw:text-slate-700">Document Type</label>
      <div class="tw:flex tw:flex-row tw:items-center tw:gap-2">
        <UISelect
          v-model="selectedDocumentTypeId"
          :options="documentTypeOptions"
          placeholder="Select a document type"
          :loading="isLoadingDocumentTypes"
          class="tw:flex-1"
          @update:modelValue="handleDocumentTypeSelect" />
        <button
          v-if="selectedDocumentTypeId"
          @click="resetDocumentTypeFilter"
          class="tw:shrink-0 tw:rounded-md tw:p-1 tw:text-slate-400 tw:transition-colors tw:hover:bg-slate-100 tw:hover:text-slate-600"
          title="Clear document type filter">
          <X class="tw:h-4 tw:w-4" />
        </button>
      </div>
    </div>

    <!-- Document List -->
    <div
      v-if="selectedAccountId"
      class="tw:space-y-2">
      <div
        v-if="isLoadingDocuments"
        class="tw:py-4 tw:text-center tw:text-sm tw:text-slate-500">
        Loading documents...
      </div>
      <div
        v-else-if="filteredDocuments.length === 0"
        class="tw:py-4 tw:text-center tw:text-sm tw:text-slate-500">
        No documents found. Please select a different client or document type.
      </div>
      <div
        v-else
        class="tw:space-y-2">
        <div class="tw:flex tw:items-center tw:gap-1">
          <h4 class="tw:text-sm tw:font-medium tw:text-slate-700">Available Documents</h4>
          <span class="tw:text-xs tw:font-normal tw:text-slate-500">({{ filteredDocuments.length }})</span>
        </div>

        <div class="tw:max-h-64 tw:space-y-2 tw:overflow-y-auto">
          <div
            v-for="doc in filteredDocuments"
            :key="doc.documentId"
            :class="[
              'tw:group tw:cursor-pointer tw:rounded-lg tw:border tw:p-3 tw:transition-all tw:duration-200',
              isDocumentSelected(doc)
                ? 'tw:border-blue-500 tw:bg-blue-50 tw:ring-2 tw:ring-blue-200'
                : 'tw:border-slate-200 tw:hover:border-slate-300 tw:hover:bg-slate-50',
            ]"
            @click="handleDocumentClick(doc)">
            <div
              :title="doc.displayName || doc.fileName"
              class="tw:flex tw:items-center tw:justify-between tw:gap-4">
              <div class="tw:flex tw:min-w-0 tw:flex-1 tw:items-center tw:gap-3">
                <span class="tw:shrink-0 tw:text-xl">{{ getFileIcon(doc.fileName) }}</span>
                <div class="tw:min-w-0 tw:flex-1">
                  <p class="tw:truncate tw:text-sm tw:font-medium tw:text-slate-900">
                    {{ doc.displayName || doc.fileName }}
                  </p>
                  <div class="tw:mt-1 tw:flex tw:flex-wrap tw:items-center tw:gap-2">
                    <Badge
                      v-if="doc.documentTypeName"
                      variant="outline"
                      class="tw:text-xs">
                      {{ doc.documentTypeName }}
                    </Badge>
                    <span class="tw:flex tw:items-center tw:gap-1 tw:text-xs tw:text-slate-500">
                      <Clock class="tw:h-3 tw:w-3" />
                      {{ formatDate(doc.createdByDate) }}
                    </span>
                  </div>
                </div>
              </div>
              <div class="tw:flex tw:shrink-0 tw:items-center tw:gap-2">
                <Badge
                  v-if="isDocumentSelected(doc)"
                  variant="secondary">
                  Selected
                </Badge>
                <button
                  v-if="doc.fileName.endsWith('.pdf') || doc.fileName.endsWith('.docx')"
                  @click="openDocument(doc, $event)"
                  class="tw:rounded-md tw:p-1 tw:text-slate-500 tw:opacity-0 tw:transition-all tw:duration-200 tw:group-hover:opacity-100 tw:hover:bg-blue-50 tw:hover:text-blue-600"
                  title="View document">
                  <Eye class="tw:h-5 tw:w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-if="!selectedAccountId"
      class="tw:py-8 tw:text-center tw:text-sm tw:text-slate-500">
      Please select a client to view available documents.
    </div>
  </div>

  <DocumentViewerModal
    :show="showDocumentModal"
    :fileName="viewDocumentFileName"
    :fetchDocument="fetchDocument"
    @update:show="showDocumentModal = $event"
    @close="showDocumentModal = false" />
</template>
